import { WrenchScrewdriverIcon } from '@heroicons/react/24/outline'
import { BookOpenIcon, HelpCircle, MessageCircle, RouteIcon, SunIcon, User } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import PaywallGate from '@/stripe/PayWallModal'
import ROUTES from '@/ROUTES'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'

import ErrorBoundary from '@/components/ErrorBoundary'
import Layout from '@/components/Layout'
import { INavLink } from '@/components/page-layouts/Links'

import logo from '../../logo-black-no-bg.svg'

interface SponsorshipResponse {
    sponsored: boolean
    orgId?: string
    orgName?: string
}

const APPLICATION_LINKS: INavLink[] = [
    { text: 'My Day', href: '/', icon: SunIcon },
    { text: 'Journey', href: '/roadmap', icon: RouteIcon },
    { text: 'Chat', href: '/chat', icon: MessageCircle },
    { text: 'Resources', href: '/resource-landing', icon: BookOpenIcon },
    { text: 'Profile', href: '/profile', icon: User },
    { text: 'Help', href: '/help', icon: HelpCircle },
]

const ADMIN_LINKS: INavLink[] = [{ text: 'Admin', href: '/admin', icon: WrenchScrewdriverIcon }]
const FOOTER_LINKS = [
    { text: 'Home', href: '/' },
    { text: 'Logout', href: '/logout' },
]

export default function AppLayout() {
    const { user } = useAuth()
    const [sponsorship, setSponsorship] = useState<SponsorshipResponse | null>(null)

    useEffect(() => {
        let cancelled = false
        async function load() {
            const response = await api<SponsorshipResponse>(ROUTES.app.sponsorship)
            if (!cancelled && response.status && response.status < 400 && response.data) {
                setSponsorship(response.data)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    return (
        <Layout
            logo={logo}
            primaryLinks={[...APPLICATION_LINKS, ...(user?.permissions?.isAdmin ? [...ADMIN_LINKS] : [])]}
            footerLinks={FOOTER_LINKS}
            variant='column'
            mobileNav='bottom'
        >
            <ErrorBoundary componentName='App'>
                {/* PaywallGate is kept for future use - control via PAYWALL_ENABLED in PayWallProvider.tsx */}
                <PaywallGate blockAccess={false}>
                    {sponsorship?.sponsored && sponsorship.orgName && (
                        <div className='w-full bg-brand-50 border-b border-brand-200 px-4 py-2 text-center text-xs text-brand-800'>
                            Sponsored by <span className='font-semibold'>{sponsorship.orgName}</span>
                        </div>
                    )}
                    <Outlet></Outlet>
                </PaywallGate>
            </ErrorBoundary>
        </Layout>
    )
}
